import React, { useState, useRef, useEffect, useCallback } from 'react';
import MagicButton from 'components/MagicButton';
import {
  useGenerateSeoReportMutation,
  useStartFullAnalysisMutation,
  useGetPipelineStatusQuery,
  useDownloadLatestMarkdownMutation,
} from 'store/services/admin/adminSeoApi';
import { downloadMarkdownFile } from 'utils/downloadArticleMarkdown';
import { toast } from 'react-toastify';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import './RunAnalysisButton.css';


const PROGRESS_STEPS = [
  'Fetching GSC data...',
  'Analyzing competitors...',
  'Building content plan...',
  'Done!',
];

const POLL_INTERVAL_MS = 20_000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

const RunAnalysisButton: React.FC = () => {
  const [generateQuick, { isLoading: isQuickLoading }] = useGenerateSeoReportMutation();
  const [startFull, { isLoading: isStartLoading }] = useStartFullAnalysisMutation();
  const [downloadLatest, { isLoading: isDownloadLoading }] = useDownloadLatestMarkdownMutation();

  const [showDropdown, setShowDropdown] = useState(false);
  const [progressStep, setProgressStep] = useState(-1);
  const [isPolling, setIsPolling] = useState(false);

  const runStartMsRef = useRef<number>(0);
  const sawRunningRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const { data: pipelineStatus } = useGetPipelineStatusQuery(undefined, {
    pollingInterval: isPolling ? POLL_INTERVAL_MS : 0,
    skip: !isPolling,
  });

  const isRunning = isQuickLoading || isStartLoading || isPolling || isDownloadLoading;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
  }, []);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  // Click outside to close dropdown
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const finishRun = useCallback((success: boolean, message: string) => {
    setIsPolling(false);
    clearTimers();
    if (success) {
      setProgressStep(3);
      toast.success(message);
      setTimeout(() => setProgressStep(-1), 3000);
    } else {
      setProgressStep(-1);
      toast.error(message);
    }
  }, [clearTimers]);

  const triggerDownload = useCallback(async (filename: string) => {
    try {
      const markdown = await downloadLatest().unwrap();
      downloadMarkdownFile(filename, markdown);
      finishRun(true, 'Markdown report downloaded');
    } catch (err: unknown) {
      console.error('[SEO] Download failed:', err);
      finishRun(false, extractErrorMessage(err, 'Download failed'));
    }
  }, [downloadLatest, finishRun]);

  // Poll watcher — detects pipeline completion and auto-downloads
  useEffect(() => {
    if (!isPolling || !pipelineStatus) return;

    const { running, latestMarkdown } = pipelineStatus;
    const latestMs = latestMarkdown ? new Date(latestMarkdown.mtime).getTime() : 0;

    if (running) {
      sawRunningRef.current = true;
      return;
    }

    if (latestMs > runStartMsRef.current) {
      const filename = latestMarkdown?.filename || `seo-report-${new Date().toISOString().slice(0, 10)}.md`;
      void triggerDownload(filename);
      return;
    }

    // running=false with no fresh file. If we never saw running=true, give it
    // a grace period for the backend flag to flip. If we did see running, the
    // pipeline finished without writing a file — treat as failure.
    if (sawRunningRef.current) {
      finishRun(false, 'Pipeline finished but no new markdown file was produced');
    }
  }, [isPolling, pipelineStatus, triggerDownload, finishRun]);

  const handleQuick = async () => {
    setShowDropdown(false);
    try {
      const result = await generateQuick().unwrap();
      if (result && 'status' in result && (result as Record<string, unknown>).status === 'error') {
        toast.error((result as Record<string, unknown>).message as string || 'Failed to generate report');
        return;
      }
      toast.success('Gap zone report updated');
    } catch (err: unknown) {
      console.error('[SEO] Quick update failed:', err);
      toast.error(extractErrorMessage(err, 'Failed to generate report'));
    }
  };

  const handleFull = async () => {
    setShowDropdown(false);
    clearTimers();
    sawRunningRef.current = false;
    runStartMsRef.current = Date.now();
    setProgressStep(0);

    // Simulated phase progression (~10 min pipeline)
    timersRef.current.push(setTimeout(() => setProgressStep(1), 60_000));
    timersRef.current.push(setTimeout(() => setProgressStep(2), 3 * 60_000));
    // Safety timeout — stop polling if pipeline never completes
    timersRef.current.push(setTimeout(() => {
      if (isPolling) {
        finishRun(false, 'Analysis timed out — check /admin/seo/latest-markdown manually');
      }
    }, POLL_TIMEOUT_MS));

    try {
      await startFull().unwrap();
      setIsPolling(true);
      toast.info('Analysis started — downloading when ready (~10 min)');
    } catch (err: unknown) {
      clearTimers();
      setProgressStep(-1);
      console.error('[SEO] Start full analysis failed:', err);
      toast.error(extractErrorMessage(err, 'Failed to start full analysis'));
    }
  };

  const handleDownloadLatest = async () => {
    setShowDropdown(false);
    try {
      const markdown = await downloadLatest().unwrap();
      const filename = pipelineStatus?.latestMarkdown?.filename
        || `seo-report-${new Date().toISOString().slice(0, 10)}.md`;
      downloadMarkdownFile(filename, markdown);
      toast.success('Markdown report downloaded');
    } catch (err: unknown) {
      console.error('[SEO] Download latest failed:', err);
      toast.error(extractErrorMessage(err, 'Download failed'));
    }
  };

  const buttonLabel = isQuickLoading
    ? 'Updating...'
    : isStartLoading
      ? 'Starting...'
      : isPolling
        ? 'Analyzing...'
        : isDownloadLoading
          ? 'Downloading...'
          : 'Run Analysis';

  return (
    <div className="run-analysis-container" ref={dropdownRef}>
      <MagicButton
        label={buttonLabel}
        onPress={() => setShowDropdown(prev => !prev)}
        color="purple"
        isLoading={isRunning}
        isDisabled={isRunning}
      />

      {showDropdown && !isRunning && (
        <div className="run-analysis-dropdown">
          <button className="run-analysis-option" onClick={handleQuick}>
            <span className="run-option-label">Quick Update</span>
            <span className="run-option-desc">GSC data only (~5s)</span>
          </button>
          <button className="run-analysis-option" onClick={handleFull}>
            <span className="run-option-label">Full Analysis</span>
            <span className="run-option-desc">GSC + competitors + content plan (~10 min, auto-downloads)</span>
          </button>
          <button className="run-analysis-option" onClick={handleDownloadLatest}>
            <span className="run-option-label">Download Latest Markdown</span>
            <span className="run-option-desc">Get the most recent report without re-running</span>
          </button>
        </div>
      )}

      {progressStep >= 0 && (
        <div className="run-progress-steps">
          {PROGRESS_STEPS.map((label, idx) => (
            <div
              key={idx}
              className={`run-progress-step ${
                idx < progressStep ? 'step-done' :
                idx === progressStep ? 'step-active' : 'step-pending'
              }`}
            >
              <span className="step-icon">
                {idx < progressStep ? '✓' : idx === progressStep ? '●' : '○'}
              </span>
              <span className="step-label">{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RunAnalysisButton;
